/**
 * MessageContextButton — icon + unread-badge trigger for a row-level message
 * thread (Rechnung/Beleg), architecture-messaging-platform.md §9.3/§9.4 +
 * design-messaging-ux.md §3.2/§3.3/§3.4.
 *
 * Renders `null` when `canUseMessaging(effectiveScopeUid)` is false (fail-closed
 * inertness — §6, beta-tenant allowlist OR general flag). Visible for BOTH
 * roles (owner AND advisor) — deliberately NOT gated on
 * `useIsReadOnly()`/`isAdvisorViewingClient`: the advisor is meant to read
 * *and* post here (architecture §9.3 "nicht unter `!isReadOnly` klammern").
 *
 * Clicking opens a slide-in Drawer (design §3.4) hosting `MessageThreadView`
 * for the given context. The Drawer owns its own open/close state, Escape-to-
 * close, and returns focus to the trigger button on close.
 *
 * @module MessageContextButton
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconMessages } from '../../icons';
import { canUseMessaging } from '../../config/messagingAccess';
import { useAuth } from '../../context/AuthContext';
import { useThreadActivity } from '../../hooks/useThreadActivity';
import { MessageContext, ReceiptRefCollection } from '../../types/messagingTypes';
import MessageThreadView, { MessageThreadContextSummary } from './MessageThreadView';

export interface MessageContextButtonProps {
  context: MessageContext;
  refCollection?: ReceiptRefCollection | null;
  /** Context-band data for the Drawer header (design §3.1); optional. */
  contextSummary?: MessageThreadContextSummary;
  className?: string;
}

/**
 * Row-level 💬-trigger. Icon reflects thread activity (design
 * §3.2, extended) via the `iconColorClass` accent (dezent when fully read,
 * stronger accent + the unread-dot overlay when not) — the wrapper's
 * `IconMessages` has no separate outline/filled variant (Icon-Sweep M-D5-4
 * Batch 7: `FaRegComments`/`FaComments` both map to the same Lucide glyph,
 * see modules-ux-icon-sweep.md §1a), so the former outline-vs-filled
 * distinction is now carried entirely by color.
 * Renders `null` — WITHOUT mounting {@link useThreadActivity} (no Firestore
 * read at all) — when `canUseMessaging(effectiveScopeUid)` is false.
 * `useAuth()` is called unconditionally so Hook order stays stable, then the
 * gate check happens in the outer {@link MessageContextButton}, before any
 * Hook in {@link MessageContextButtonInner} runs (same outer/inner split as
 * `ReceiptScanPage`'s access gate) — this is what makes the feature fully
 * inert, not just visually hidden.
 */
const MessageContextButton: React.FC<MessageContextButtonProps> = (props) => {
  const { effectiveScopeUid } = useAuth();
  if (!canUseMessaging(effectiveScopeUid)) {
    return null;
  }
  return <MessageContextButtonInner {...props} />;
};

const MessageContextButtonInner: React.FC<MessageContextButtonProps> = ({
  context,
  refCollection,
  contextSummary,
  className,
}) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { unread, hasMessages } = useThreadActivity(context);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  // Icon state (design-messaging-ux.md §3.2, extended): no discussion yet →
  // dampened outline icon; discussion exists but fully read → filled icon in a
  // dezent accent (no dot); discussion exists with unread → filled icon in a
  // stronger accent + the existing unread dot.
  const label =
    unread > 0
      ? t('messaging.openThreadUnread', { count: unread, defaultValue: 'Verlauf öffnen ({{count}} ungelesen)' })
      : hasMessages
        ? t('messaging.openThreadHasDiscussion', 'Verlauf öffnen (Diskussion vorhanden)')
        : t('messaging.openThreadNoDiscussion', 'Verlauf öffnen (noch keine Nachrichten)');

  const iconColorClass = unread > 0 ? 'text-accent-ink' : hasMessages ? 'text-accent-ink' : 'text-muted';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        title={label}
        aria-label={label}
        className={`relative p-1.5 rounded ${iconColorClass} hover:text-accent-ink hover:bg-surface-hover transition-colors ${className ?? ''}`}
      >
        <IconMessages size={16} />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-indigo-500 ring-2 ring-[var(--surface-raised)]"
          />
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={close}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('messaging.title')}
            className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-surface-raised shadow-xl z-50 flex flex-col
                       transition-transform duration-200 ease-out motion-reduce:transition-none"
          >
            <MessageThreadView
              context={context}
              refCollection={refCollection}
              contextSummary={contextSummary}
              onClose={close}
            />
            <div className="shrink-0 border-t border-hairline px-4 py-2">
              <a
                href={`/dashboard/nachrichten?thread=${encodeURIComponent(`${context.type}:${context.refId ?? '_'}`)}`}
                className="text-accent-ink hover:text-accent-ink text-sm"
              >
                {t('messaging.openInCenter', 'Im Nachrichten-Center öffnen →')}
              </a>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MessageContextButton;
